"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { AppShell } from "@/components/app-shell";
import { AppLogo } from "@/components/icons";
import { useAuth } from "@/context/auth-context";
import { useHasOnboardingData } from "@/hooks/use-has-onboarding-data";

/** Routes that render full-screen without the sidebar/app-shell. */
const FULL_SCREEN_PREFIXES = ["/beta-launch", "/welcome", "/subscribe", "/login", "/privacy", "/terms"];

function isFullScreenRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  return FULL_SCREEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
  );
}

function LoadingSplash() {
  return (
    <div className="flex items-center justify-center h-screen">
      <AppLogo iconSize="size-10" textSize="text-2xl" />
    </div>
  );
}

/**
 * Wraps children with AppShell only for app routes (dashboard, games, etc.).
 * Full-screen routes (/beta-launch/*, /welcome, /subscribe, /login) skip the sidebar.
 *
 * Gate order for logged-in users:
 *  1. Subscription check  -> redirect to /subscribe if unpaid
 *  2. Onboarding check    -> redirect to /welcome if no data
 *
 * All gates resolve before the shell is mounted, so the user sees at most
 * one loading splash instead of sequential logo -> logo -> logo -> shell.
 */
export function AppShellWrapper({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const {
    user,
    loading: authLoading,
    hasActiveSubscription,
    subscriptionLoading,
  } = useAuth();
  const { hasData, loading: onboardingCheckLoading } =
    useHasOnboardingData(user);

  const isFullScreen = isFullScreenRoute(pathname);

  // Tracks whether we're mid-redirect so we don't flash the shell before navigation completes
  const redirectingRef = useRef(false);

  const allGatesLoading = authLoading || subscriptionLoading || onboardingCheckLoading;

  // Gate 1: Redirect unpaid users to /subscribe
  useEffect(() => {
    if (allGatesLoading || !user || isFullScreen) return;
    if (!hasActiveSubscription) {
      redirectingRef.current = true;
      router.replace("/subscribe");
    }
  }, [allGatesLoading, user, hasActiveSubscription, isFullScreen, router]);

  // Gate 2: Redirect users with no data to /welcome (only after subscription is confirmed)
  useEffect(() => {
    if (allGatesLoading || !user || isFullScreen) return;
    if (hasActiveSubscription && hasData === false) {
      redirectingRef.current = true;
      router.replace("/welcome");
    }
  }, [allGatesLoading, user, hasActiveSubscription, hasData, isFullScreen, router]);

  // Reset redirect flag when we arrive at a full-screen route (redirect completed)
  useEffect(() => {
    if (isFullScreen) redirectingRef.current = false;
  }, [isFullScreen]);

  // Full-screen routes: render without shell
  if (isFullScreen) {
    return <>{children}</>;
  }

  // Not logged in
  if (!user) {
    // While auth is resolving, or on non-root protected routes, show a single splash
    if (authLoading || pathname !== "/") {
      return <LoadingSplash />;
    }
    // Root landing page renders without shell
    return <>{children}</>;
  }

  // Logged-in but gates still loading, or a redirect is in flight
  const needsRedirect = !hasActiveSubscription || hasData === false;
  if (allGatesLoading || needsRedirect || redirectingRef.current) {
    return <LoadingSplash />;
  }

  return <AppShell>{children}</AppShell>;
}
